import { fetchUtils } from 'react-admin';
import { stringify } from 'query-string';
import formatData from './formatData';
import { apiUrl } from '../constants';

export const fetchJson = (url: string, options = {}) => {
  const token = localStorage.getItem('auth') as string;
  return fetchUtils.fetchJson(url, {
    ...options,
    user: {
      authenticated: !!token,
      token,
    },
  });
};

export default {
  getList: (
    resource: string,
    params: {
      pagination: { page: any; perPage: any };
      sort: { field: any; order: any };
      filter: any;
      meta?: any;
    }
  ) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      sort: JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify(params.filter),
      ...params.meta,
    };

    const url = `${apiUrl}/billerMapping?${stringify(query)}`;

    return fetchJson(url).then(({ headers, json }) => {
      let dataArray = [];

      if (json) {
        // Extract data array from response
        if (Array.isArray(json)) {
          dataArray = json;
        } else if (json.data && Array.isArray(json.data)) {
          dataArray = json.data;
        } else if (typeof json === 'object') {
          dataArray = [json];
        }

        // Apply formatter
        dataArray = dataArray.map((item: any) => formatData(resource, item));
      }

      const total = parseInt(
        headers.get('content-range')?.split('/').pop() || dataArray.length.toString(),
        10
      );

      return {
        data: dataArray,
        total: total < dataArray.length ? dataArray.length : total,
      };
    });
  },

  getOne: (resource: string, params: { id: any }) => {
    return fetchJson(`${apiUrl}/billerMapping/${params.id}`).then(({ json }) => {
      let data = json;
      if (json.data) {
        // Check if data is a JSON string and parse it
        if (typeof json.data === 'string') {
          try {
            data = JSON.parse(json.data);
          } catch (e) {
            console.error('Error parsing JSON string:', e);
            data = json.data;
          }
        } else {
          data = json.data;
        }
      }
      return {
        data: formatData(resource, data),
      };
    });
  },

  getMany: (resource: string, params: { ids: any }) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    const url = `${apiUrl}/billerMapping?${stringify(query)}`;
    return fetchJson(url).then(({ json }) => {
      let dataArray = Array.isArray(json) ? json : (json.data || []);
      return {
        data: dataArray.map((d: any) => formatData(resource, d)),
      };
    });
  },

  getManyReference: (
    resource: string,
    params: {
      pagination: { page: any; perPage: any };
      sort: { field: any; order: any };
      filter: any;
      target: any;
      id: any;
    }
  ) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      sort: JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify({
        ...params.filter,
        [params.target]: params.id,
      }),
    };
    const url = `${apiUrl}/billerMapping?${stringify(query)}`;

    return fetchJson(url).then(({ headers, json }) => {
      let dataArray = Array.isArray(json) ? json : (json.data || []);
      return {
        data: dataArray.map((d: any) => formatData(resource, d)),
        total: parseInt(
          headers.get('content-range')?.split('/').pop() || dataArray.length.toString(),
          10
        ),
      };
    });
  },

  update: (resource: string, params: { id: any; data: any }) => {
    return fetchJson(`${apiUrl}/billerMapping/${params.id}`, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => {
      if (json.code == 'MSG_0050') {
        throw new Error(json.data);
      }
      // PUT returns success message, return the updated data with ID
      const updatedData = {
        ...params.data,
        id: params.id,
        updatedAt: new Date().toISOString(),
      };
      return { data: formatData(resource, updatedData) };
    });
  },

  updateMany: (resource: string, params: { ids: any; data: any }) => {
    // For billerMapping, the PUT endpoint expects { ids: [], merchantCode: "" }
    return fetchJson(`${apiUrl}/billerMapping`, {
      method: 'PUT',
      body: JSON.stringify({
        ids: params.ids,
        merchantCode: params.data.merchantCode,
      }),
    }).then(({ json }) => {
      if (json.code == 'MSG_0050') {
        throw new Error(json.data);
      }
      // Return the ids that were updated
      return { data: params.ids };
    });
  },

  create: (resource: string, params: { data: any }) => {
    return fetchJson(`${apiUrl}/billerMapping`, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => {
      if (json.code == 'MSG_0050') {
        throw new Error(json.data);
      }
      // POST returns success message, not the created object
      // Generate a temporary ID and return the submitted data
      const createdData = {
        ...params.data,
        id: `temp_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      return {
        data: formatData(resource, createdData),
      };
    });
  },

  delete: (resource: string, params: { id: any }) => {
    return fetchJson(`${apiUrl}/billerMapping/${params.id}`, {
      method: 'DELETE',
    }).then(({ json }) => {
      let data = json;
      if (json.data) {
        data = json.data;
      }
      return { data: formatData(resource, data) };
    });
  },

  deleteMany: (resource: string, params: { ids: any }) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return fetchJson(`${apiUrl}/billerMapping?${stringify(query)}`, {
      method: 'DELETE',
    }).then(({ json }) => {
      let dataArray = Array.isArray(json) ? json : (json.data || []);
      return {
        data: dataArray.map((d: any) => formatData(resource, d)),
      };
    });
  },
};
